import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';

import { useAuth } from '../../components/AuthContext';
import { canEditInventoryItem, canAdjustInventory, canManageRequests as canManageRequestsPermission, isBranchScopedManager, isAdminManagerOrExecutive, canSeeInventoryCost, canSetProductionCost } from '../../lib/permissions';
import { resolveFulfilment, requestRoute, plainTransferRefusal, transfersInHand } from '../../lib/branchTransfers';
import { onItsWayTo, onItsWayLine, ON_ITS_WAY_STATUSES } from '../../lib/transferLoads';
import { cutOff } from '../../lib/readLimit';
import { branchMakes, undoRecordProduction, recordedLine } from '../../lib/production';
import ShelfRunAnswer from '../../components/inventory/ShelfRunAnswer';
import { CORRECTED_LABEL, isCorrectionTakeBack, fetchShelfRunFacts, shelfRunCorrectable } from '../../lib/corrections';
import CorrectionSheet from '../../components/inventory/CorrectionSheet';
import { CorrectButton } from '../../components/inventory/TransferLoadPanels';
import { useTier } from '../../lib/tiers';
import RecordProductionSheet, { SetProductionCostPanel } from './RecordProductionSheet';
// #349, Session 79.22b.6.1g -- Use and Adjust save through the one shared copy.
import { submitUseStock, submitAdjustStock, stockUseRefusal, USE_NEEDS_WO, adjustDoneLine } from '../../lib/stockMovements';
import AdjustAlertNotice from '../../components/inventory/AdjustAlertNotice';
import { submitRestockRequest as createRestockRequest } from '../../lib/materialRequests';
import { submitReceiveStock, resolveReceivePrintOption, fetchPackSnapshot } from '../../lib/materialReceive';
import { AddItemModal, EditSpecsModal } from './InventoryModals';
import { categoryLabel } from '../../lib/categoryLabel';
import { getStockStatus } from '../../lib/stockStatus';
import { workOrderRefError, WORK_ORDER_REF_HINT } from '../../lib/workOrderRef';
import { ADJUST_REASONS, REASON_OPENING_COUNT } from '../../lib/adjustReasons';
import {
  IconArrowLeft,
  IconPhoto,
  IconPhotoOff,
  IconUpload,
  IconArchive,
  IconTrash,
  IconEdit,
  IconCheck,
  IconX,
  IconPackage,
  IconCamera,
  IconAlertTriangle,
  IconInfoCircle,
  IconHistory,
} from '@tabler/icons-react';
import VendorItemsPanel from '../../components/inventory/VendorItemsPanel';
import MaterialRequestsPanel from '../../components/inventory/MaterialRequestsPanel';
import ReceiveSheetContent from '../../components/inventory/ReceiveSheetContent';
import LabelCard from '../../components/inventory/LabelCard';
import QtyStepper, { fieldBox, NEGATIVE_BOX } from '../../components/QtyStepper';
import Overlay from '../../components/Overlay';
import StoredImage from '../../components/StoredImage';
import { INVENTORY_BUCKET } from '../../lib/storedFile';

const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];
const BRANCH_NAMES = Object.fromEntries(BRANCHES.map(b => [String(b.id), b.name]));

// #512, Session 79.22b.6.1f -- APPROVED DRAWING B. Said in place of the
// arriving lines when the check could not run (B4): a blank would read as
// "nothing is coming", which is a claim the screen could not make.
const INCOMING_FAILED = 'Couldn’t check what’s on its way.';
// Stock Home's own style for these lines (its comingBlock()), so the two read
// the same; 11px on phone and desk alike.
const COMING_LINE = { fontSize: 11, color: '#14503B', fontWeight: 600, lineHeight: 1.3, marginTop: 2 };

const TX_TYPE_STYLES = {
  receive:      { bg: '#E1F5EE', color: '#085041', label: 'Receive' },
  consume:      { bg: '#FAECE7', color: '#712B13', label: 'Consume' },
  adjust:       { bg: '#EEEDFE', color: '#3C3489', label: 'Adjust'  },
  transfer_in:  { bg: '#E6F1FB', color: '#0C447C', label: 'Transfer in' },
  transfer_out: { bg: '#FAEEDA', color: '#633806', label: 'Transfer out' },
  // #371 D23. Without this the badge falls back to the raw enum value or to
  // nothing at all, on the screen a person opens to ask what happened to an item.
  production_in: { bg: '#E1F5EE', color: '#085041', label: 'Made' },
  // #694. An Undo takes back the row it names; its sign is in its own qty,
  // which fmtQty below already shows as it is.
  reversal:     { bg: '#F1EFE8', color: '#5F5E5A', label: 'Undone' },
};

// #761 / gate B6 -- a take-back a correction wrote reads Corrected, not Undone.
function txStyleFor(tx) {
  const ts = TX_TYPE_STYLES[tx.tx_type] || { bg: '#F0F0F0', color: '#5C5C5C', label: tx.tx_type };
  return isCorrectionTakeBack(tx) ? { ...ts, label: CORRECTED_LABEL } : ts;
}

// #343, Session 78m. D9's other half -- Item Detail declares when the value it
// is showing is still an estimate.
//
// The Ledger says it per movement; this says it per ITEM, because the question a
// manager actually asks here is "can I trust this number", not "which receipt".
//
// Renders nothing when the viewer cannot see cost at all, and nothing when there
// is no cost to describe. A zero-state reading "0 provisional" would be noise on
// every item nobody has ever priced -- which at D&R is most of them.
//
// #656, Session 79.22b.1 -- AND IT SAID INVOICES HAD SETTLED A FIGURE NO
// INVOICE HAS EVER TOUCHED.
//
// Dallas's Stake DT 16x21-48 is a stake D&R makes; every one Dallas holds came
// from Houston on a transfer. Its page read "Cost basis $4.77 - Every receipt
// of this item has been settled by a vendor invoice." True only in the sense
// that none of ZERO receipts is unsettled, and it tells a manager the number
// was checked against invoices when it was not.
//
// #634 makes it the common case rather than a curiosity: counting a transfer in
// now sets the receiving branch's average from the cost that travelled with it,
// so every made item at a receiving branch gains a cost basis and would have
// carried this sentence.
//
// THE THIRD SENTENCE SAYS WHERE THE FIGURE CAME FROM, and it is split in two
// because one wording could not be true of both. A branch that has taken
// transfers gets the transfers named; a branch with neither receipts nor
// transfers gets a statement about invoices and no claim about origin, because
// there is nothing here that knows what set the average in that case. Guessing
// would be the defect this entry is about, pointed the other way.
//
// The settled and provisional sentences are untouched: #343's D9 is that an
// item with unsettled receipts still says its figure is an estimate.
// #656 -- THE SENTENCE UNDER THE FIGURE, AS A FUNCTION, so the four cases can
// be tested without a screen. It was one ternary inside the markup and the
// wrong branch of it was a claim about money.
//
// `vendorReceipts` null means NOT COUNTED YET, and that is not the same as
// zero. costSetter's counts are a round trip; flashing "nothing here was bought
// from a vendor" while they are in flight would assert something false about
// money for as long as the query takes. Until they answer, the strip keeps the
// sentence it has always had.
export function costBasisSentence({ provisionalCount = 0, vendorReceipts = null, transfersIn = null } = {}) {
  if (provisionalCount > 0) {
    // #343's D9, untouched: an item with unsettled receipts still says its
    // figure is an estimate, and that outranks where the figure came from.
    return provisionalCount + (provisionalCount === 1 ? ' receipt has' : ' receipts have')
      + ' not been settled by a vendor invoice yet, so this figure is still an estimate.';
  }
  if (vendorReceipts === 0) {
    // Split in two because one wording could not be true of both. A branch that
    // has taken transfers gets the transfers named; a branch with neither gets
    // a statement about invoices and no claim about origin, because nothing
    // here knows what set the average in that case. Guessing would be this
    // entry's own defect pointed the other way.
    return transfersIn > 0
      ? 'Nothing here was bought from a vendor. This figure is the cost that travelled in with transfers.'
      : 'Nothing here was bought from a vendor, so no invoice settles this figure.';
  }
  return 'Every receipt of this item has been settled by a vendor invoice.';
}

function CostBasisStrip({ avgCost, provisionalCount, vendorReceipts, transfersIn, compact }) {
  if (avgCost == null && !provisionalCount) return null;

  const provisional = provisionalCount > 0;

  return (
    <div style={{
      background: provisional ? '#FFFAF0' : '#F7F7F7',
      border: '1px solid ' + (provisional ? '#F0DFC0' : '#E8E8E8'),
      borderRadius: 10, padding: compact ? '8px 12px' : '10px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '.7px' }}>
          Cost basis
        </span>
        <span style={{ fontSize: compact ? 15 : 16, fontWeight: 700, color: '#0F0F0F' }}>
          {avgCost != null
            ? '$' + Number(avgCost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : 'Not valued'}
        </span>
      </div>
      <div style={{ fontSize: 11, color: provisional ? '#8A6100' : '#7A7A7A', lineHeight: 1.5, marginTop: 4 }}>
        {costBasisSentence({ provisionalCount, vendorReceipts, transfersIn })}
      </div>
    </div>
  );
}

// #515 D32, Session 79.8 — what it costs D&R to MAKE one of these.
//
// A DIFFERENT NUMBER FROM THE COST BASIS STRIP ABOVE, AND THE TWO MUST NEVER
// BE CONFLATED. Cost basis is an AVERAGE of what we paid suppliers, which is
// the right instrument for something you buy. Houston never bought a stake, so
// that figure is null for anything D&R makes and stays null forever — which is
// exactly how a hundred stakes arrived at Dallas valued at nothing (79.6).
//
// It renders on made items only. It also renders when the cost is NOT SET,
// unlike the cost-basis strip, and that is deliberate rather than inconsistent:
// an unpriced purchase is ordinary, while a made item with no cost is the
// defect this whole issue exists about. Saying "Not set" is the warning.
function CostToMakeStrip({ item, canChange, onChange, compact }) {
  const cost = item?.standard_cost;
  const unset = cost == null;
  return (
    <div style={{
      background: unset ? '#FFF7E8' : '#F7F7F7',
      border: '1px solid ' + (unset ? '#F0DCB4' : '#E8E8E8'),
      borderRadius: 10, padding: compact ? '8px 12px' : '10px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '.7px' }}>
          Cost to make
        </span>
        <span style={{ fontSize: compact ? 15 : 16, fontWeight: 700, color: unset ? '#8A6100' : '#0F0F0F' }}>
          {unset
            ? 'Not set'
            : '$' + Number(cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>
      <div style={{ fontSize: 11, color: unset ? '#8A6100' : '#7A7A7A', lineHeight: 1.5, marginTop: 4 }}>
        {unset
          ? 'We make this and nothing says what it costs, so anything sent to another branch arrives valued at nothing.'
          : 'What it costs us to make one. Not what we sell it for.'}
        {canChange && (
          <>
            {' '}
            <button
              type="button"
              onClick={onChange}
              style={{
                background: 'none', border: 'none', padding: 0, font: 'inherit',
                color: '#1D9E75', textDecoration: 'underline', cursor: 'pointer',
              }}
            >
              {unset ? 'Set it' : 'Change'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const STATUS_STYLES = {
  OK:  { color: '#1D9E75', dot: '#1D9E75' },
  // #658: text #9A5F00; dot keeps #BA7517. The pill gets the catalog's own
  // Low ground -- tinting it from the text colour, as the others do, gives
  // #9A5F00 only 4.39, and the old amber 3.21.
  Low: { color: '#9A5F00', bg: '#FFF3E0', dot: '#BA7517' },
  Out: { color: '#A32D2D', dot: '#A32D2D' },
};

// #357 — a FOURTH copy of the status rule, carrying the same inverted test as
// the other three: the reorder point was checked before the quantity, so this
// page's own status badge read blank rather than "Out" for an item at zero with
// no threshold. #357 names three screens; this one was found by sweeping for the
// pattern rather than by working the list, per locked rule 11. The rule now
// lives in src/lib/stockStatus.js.

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtQty(qty) {
  if (qty === null || qty === undefined) return '—';
  const n = Number(qty);
  return n > 0 ? `+${n}` : String(n);
}



// ── USE SHEET CONTENT (top-level) ──────────────────────────────────────────
// Same pattern as ReceiveSheetContent: owns its own form state, only reports
// upward on submit. fetchJobHistory stays a parent-owned async fn passed in
// as a prop, since it hits Supabase and the result needs to live somewhere
// that survives this component's own state resets.

function UseSheetContent({
  itemName,
  branchName,
  homeStockQty,
  itemUom,
  onFetchJobHistory,
  onSubmit,
  onCancel,
  saving,
  done,
  serverError,
  requireJobRef,
}) {
  const [qty, setQty] = useState(1);
  const [jobRef, setJobRef] = useState('');
  const [notes, setNotes] = useState('');
  const [jobHistory, setJobHistory] = useState(null);
  const [jobHistoryLoading, setJobHistoryLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const error = localError || serverError;
  const qtyInt = parseInt(qty) || 0;
  const newQty = homeStockQty - qtyInt;
  const willGoNegative = newQty < 0;

  async function handleJobRefChange(val) {
    setJobRef(val);
    if (!val.trim()) { setJobHistory(null); return; }
    setJobHistoryLoading(true);
    const history = await onFetchJobHistory(val.trim());
    setJobHistory(history);
    setJobHistoryLoading(false);
  }

  function handleSubmit() {
    const parsedQty = parseInt(qty);
    // #349, Session 79.22b.6.1g -- the shared Use's own check, so this sheet and
    // the Use screen refuse in the same words: the quantity, #499's WO# format,
    // #366's WO#-or-note pair, and #199's switch when it is on (drawing D).
    const refusal = stockUseRefusal({ qty: parsedQty, jobRef, notes, requireJobRef });
    if (refusal) { setLocalError(refusal); return; }
    setLocalError('');
    onSubmit({ qty: parsedQty, jobRef: jobRef.trim(), notes: notes.trim(), willGoNegative, newQty });
  }

  if (done) {
    return (
      <>
        <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', marginBottom: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>Stock updated</div>
          <div style={{ fontSize: 12, color: '#5C5C5C' }}>
            {willGoNegative ? 'Stock went negative. Your manager has been notified to audit this item.' : 'Inventory count has been updated.'}
          </div>
        </div>
        <button onClick={onCancel} style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer' }}>Done</button>
      </>
    );
  }

  return (
    <>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginBottom: 2 }}>Use stock</div>
      <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 16 }}>
        {itemName} · {branchName} · <span style={{ fontWeight: 700, color: willGoNegative ? '#A32D2D' : '#0F0F0F' }}>{homeStockQty} on hand</span>
      </div>

      {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}

      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 8 }}>Qty used</div>
      {/* #760 - the shared stepper (approved drawing, section 3); the box keeps
          its red when stock will go negative. Whole numbers only: a typed 2.5
          is refused instead of quietly used as 2. */}
      <QtyStepper value={String(qty ?? '')} onChange={setQty} min={1} label="Qty used"
        boxStyle={fieldBox(80, 44, 22, willGoNegative ? NEGATIVE_BOX : null)} rowStyle={{ marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: willGoNegative ? '#A32D2D' : '#1D9E75', fontWeight: 700, flexShrink: 0 }}>
          → {newQty} remaining
        </div>
      </QtyStepper>

      {willGoNegative && (
        <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 8, padding: '10px 12px', marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <IconAlertTriangle size={15} color="#A32D2D" style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#A32D2D' }}>Stock will go negative</div>
            <div style={{ fontSize: 11, color: '#791F1F', marginTop: 1 }}>Your manager will be notified to audit this item. You can still submit.</div>
          </div>
        </div>
      )}

      {/* #366, Session 78g. Both fields read "(optional)" while handleSubmit()
          refuses the submit unless one of them is filled — the #277 shape one
          screen over, resolved at 74f on the receive path and never swept here.
          The requirement is a disjunction ACROSS two fields, so no per-field
          label can state it correctly; it is stated once, above the pair, and
          in the same words as the error the submit returns. */}
      {/* #199 part 1 (drawing D, D2-D3): with the company's switch on, a WO# alone. */}
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 10 }}>
        {requireJobRef
          ? <>A <strong style={{ color: '#5C5C5C' }}>WO#</strong> is required.</>
          : <>Enter a <strong style={{ color: '#5C5C5C' }}>WO#</strong> or a <strong style={{ color: '#5C5C5C' }}>note</strong>, at least one is required.</>}
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', marginBottom: 6 }}>WO#{requireJobRef ? ' *' : ''}</div>
      <input type="text" value={jobRef}
        onChange={e => handleJobRefChange(e.target.value)}
        placeholder="e.g. 123456 or 123456-7"
        style={{ width: '100%', height: 40, border: (workOrderRefError(jobRef) || error === USE_NEEDS_WO) ? '1.5px solid #A32D2D' : '1px solid #E0E0E0', borderRadius: 10, padding: '0 12px', fontSize: 16, color: '#0F0F0F', marginBottom: workOrderRefError(jobRef) ? 4 : (jobHistory ? 8 : 16), boxSizing: 'border-box' }} />

      {/* #499 - beside the field, as they type. */}
      {workOrderRefError(jobRef) && (
        <div style={{ fontSize: 11, color: '#A32D2D', marginBottom: 12 }}>{WORK_ORDER_REF_HINT}</div>
      )}

      {jobHistoryLoading && (
        <div style={{ fontSize: 11, color: '#9A9A9A', marginBottom: 12 }}>Looking up job history...</div>
      )}

      {jobHistory && !jobHistoryLoading && (
        <div style={{ background: '#EEEDFE', border: '1px solid #AFA9EC', borderRadius: 8, padding: '10px 12px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <IconHistory size={15} color="#3C3489" style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#3C3489' }}>{jobHistory.totalUsed} {itemUom} already used on {jobRef.trim()}</div>
            <div style={{ fontSize: 11, color: '#534AB7', marginTop: 1 }}>{jobHistory.txCount} transaction{jobHistory.txCount !== 1 ? 's' : ''} · last pull {fmtDate(jobHistory.lastDate)}</div>
          </div>
        </div>
      )}

      {/* #366 — "(optional)" removed; the pair's rule is stated above the WO# field. */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', marginBottom: 6 }}>Notes{requireJobRef && <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--ink-3)' }}> (optional)</span>}</div>
      <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. cut for face panel"
        style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 10, padding: '0 12px', fontSize: 16, color: '#0F0F0F', marginBottom: 20, boxSizing: 'border-box' }} />

      <button onClick={handleSubmit} disabled={saving}
        style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: saving ? '#E0E0E0' : willGoNegative ? '#A32D2D' : '#0F0F0F', color: saving ? '#9A9A9A' : 'white', cursor: saving ? 'not-allowed' : 'pointer', marginBottom: 10 }}>
        {saving ? 'Saving...' : willGoNegative ? 'Submit anyway' : 'Confirm use'}
      </button>
      <button onClick={onCancel} style={{ width: '100%', background: 'none', border: 'none', fontSize: 13, color: '#7A7A7A', cursor: 'pointer', padding: 4 }}>Cancel</button>
      <div style={{ fontSize: 11, color: '#9A9A9A', textAlign: 'center', marginTop: 8 }}>Updates stock count immediately</div>
    </>
  );
}

// #364, Session 78g. Receive and Use both open a sheet in place on this page;
// Adjust was the odd one out — absent on desktop, and on mobile it called
// setActive('inv_adjust'), which throws you to a different screen and loses the
// item you were looking at. This page is the natural home for all four, because
// it is the one screen already showing stock by branch, the ledger and the
// vendor: everything a person needs to decide what the count should be.
//
// #364's own Owner line says AdjustSheet "already exists in
// InventoryActionCatalog.js" and the work is lifting it. It does not exist —
// that file's adjust UI is inline JSX inside its main render, not a component.
// So this is written to match UseSheetContent above, which is this file's own
// convention. Extracting ONE sheet shared by both screens is the right end
// state and belongs with #349's consolidation pass (Sep 4), which already owns
// "use stock implemented end to end in two files" — the identical shape, in the
// identical pair of files. Doing it here would collide with #363's and #349's
// scheduled work in InventoryActionCatalog.js.
//
// Same contract as UseSheetContent: owns its form state, reports up on submit.
function AdjustSheetContent({
  itemName,
  branchName,
  currentQty,
  itemUom,
  userName,
  onSubmit,
  onCancel,
  saving,
  done,
  doneReason,
  doneLine,
  serverError,
  itemId,
  branchId,
  canSeeCost,
}) {
  const [qty, setQty] = useState(String(currentQty ?? 0));
  const [reason, setReason] = useState(ADJUST_REASONS[0]);
  const [notes, setNotes] = useState('');
  const [localError, setLocalError] = useState('');

  const error = localError || serverError;
  const parsed = parseInt(qty);
  const delta = Number.isNaN(parsed) ? 0 : parsed - (currentQty ?? 0);

  function handleSubmit() {
    const newQty = parseInt(qty);
    if (Number.isNaN(newQty) || newQty < 0) {
      setLocalError('Enter the number you physically counted.');
      return;
    }
    if (!reason) {
      setLocalError('Please select a reason.');
      return;
    }
    setLocalError('');
    onSubmit({ newQty, reason, notes: notes.trim() });
  }

  if (done) {
    return (
      <>
        <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', marginBottom: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>Count adjusted</div>
          {/* #369 — an Opening count notifies nobody, so it must not claim to. */}
          {/* #363 D17 -- from what the save did: "notified" only when an alert was delivered. */}
          <div style={{ fontSize: 12, color: '#5C5C5C' }}>
            {doneLine || (doneReason === REASON_OPENING_COUNT
              ? 'Inventory updated and recorded in the ledger.'
              : 'Inventory updated and your manager has been notified.')}
          </div>
        </div>
        <button onClick={onCancel} style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer' }}>Done</button>
      </>
    );
  }

  return (
    <>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F', marginBottom: 2 }}>Adjust count</div>
      <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 12 }}>{itemName} · {branchName}</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 6, padding: '3px 10px', fontSize: 12, color: '#A32D2D', fontWeight: 700 }}>
          System: {currentQty ?? 0}
        </div>
        <div style={{ fontSize: 13, color: '#7A7A7A' }}>→ enter actual count below</div>
      </div>

      {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}

      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 8 }}>Actual qty on hand</div>
      {/* #760 - the shared stepper (approved drawing, section 3). */}
      <QtyStepper value={qty} onChange={setQty} min={0} label="Actual qty on hand"
        boxStyle={fieldBox(90, 52, 26)} rowStyle={{ marginBottom: 20, flexWrap: 'wrap' }}>
        {/* The running delta is not in the InventoryActionCatalog version. It is
            the number that actually gets written to the ledger, and on an opening
            count it is the difference the branch is about to book. */}
        {delta !== 0 && (
          <div style={{ fontSize: 12, fontWeight: 700, color: delta > 0 ? '#1D9E75' : '#A32D2D', flexShrink: 0 }}>
            {delta > 0 ? '+' : ''}{delta} {itemUom}
          </div>
        )}
      </QtyStepper>

      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 8 }}>Reason *</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
        {ADJUST_REASONS.map(r => (
          <button key={r} onClick={() => setReason(r)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: `1px solid ${reason === r ? '#1D9E75' : '#E0E0E0'}`, background: reason === r ? '#E1F5EE' : '#F8F8F8', cursor: 'pointer', textAlign: 'left' }}>
            <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${reason === r ? '#1D9E75' : '#C0C0C0'}`, background: reason === r ? '#1D9E75' : 'white', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {reason === r && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white' }} />}
            </div>
            <span style={{ fontSize: 13, color: reason === r ? '#085041' : '#0F0F0F', fontWeight: reason === r ? 700 : 400 }}>{r}</span>
          </button>
        ))}
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>Notes <span style={{ fontWeight: 400, textTransform: 'none', color: '#9A9A9A' }}>(optional)</span></div>
      <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. counted during morning walkthrough"
        style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 10, padding: '0 12px', fontSize: 16, color: '#0F0F0F', marginBottom: 20, boxSizing: 'border-box' }} />

      {/* #363 D3 -- whether the managers will be notified, said before Confirm. */}
      <AdjustAlertNotice itemId={itemId} branchId={branchId} branchName={branchName}
        onHand={currentQty ?? 0} counted={qty} reason={reason} canSeeCost={canSeeCost} />

      <button onClick={handleSubmit} disabled={saving}
        style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: saving ? '#E0E0E0' : '#0F0F0F', color: saving ? '#9A9A9A' : 'white', cursor: saving ? 'not-allowed' : 'pointer', marginBottom: 10 }}>
        {saving ? 'Saving...' : 'Confirm adjustment'}
      </button>
      <button onClick={onCancel} style={{ width: '100%', background: 'none', border: 'none', fontSize: 13, color: '#7A7A7A', cursor: 'pointer', padding: 4 }}>Cancel</button>
      <div style={{ fontSize: 11, color: '#9A9A9A', textAlign: 'center', marginTop: 8 }}>
        Logged as {userName} · {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </div>
    </>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

export default function InventoryItemDetail({ itemId, setActive, setFilterItemId, setFilterItemName, setOpenRequestId, setScanItemId }) {
  const { profile, isAdmin, isManager, isExecutive, isTeamMember, isPurchasing, requireJobRefOnUse } = useAuth();
  const isMobile = window.innerWidth <= 768;
  // #448 — isExecutive added; see the note at the catalog's canAddItem.
  // #422/78t -- `profile` added; see the note in permissions.js. Without it
  // a branch manager cannot edit an item they are allowed to edit.
  const canEdit = canEditInventoryItem({ isAdmin, isManager, isExecutive, profile });
  // #369 — passes the whole auth object, not the manager triple. See permissions.js.
  const canAdjust = canAdjustInventory({ isAdmin, isManager, isExecutive, isTeamMember, isPurchasing, profile });
  const canManageRequests = canManageRequestsPermission({ isAdmin, isManager, isExecutive });
  // #343, Session 78m. GATED DELIBERATELY, and the gate is load-bearing rather
  // than routine. Session 78l granted team members read-only Catalog access on
  // the stated premise that "neither Catalog nor Item Detail renders a cost
  // figure" -- see #77. This screen now renders one, so that premise survives
  // only because canSeeInventoryCost excludes team_member. Do not widen this
  // predicate without reopening #77.
  // #488, Session 78w — passes the whole auth object, not the manager triple.
  // This predicate now reaches accounts payable through canReconcileInvoice,
  // which reads `isPurchasing` and `profile.invoice_reconciliation_enabled`.
  // Passing only the triple would hand it an undefined profile and silently
  // give AP nothing. See permissions.js.
  const canSeeCost = canSeeInventoryCost({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  // #515 D32. LOCKED RULE 8: the whole auth object, never the role triple —
  // this predicate reads `profile.production_cost_actions_enabled` and a call
  // site passing only the flags hands it an undefined profile, which reads as
  // false for every manager and makes the control silently never appear.
  const maySetProductionCost = canSetProductionCost({ isAdmin, isManager, isExecutive, profile });
  const isBranchScoped = isBranchScopedManager({ isAdmin, isManager, isExecutive });
  const canOverrideJobRef = isAdminManagerOrExecutive({ isAdmin, isManager, isExecutive });

  const [item, setItem] = useState(null);
  const [stockRows, setStockRows] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  // #515 D32 — the "set what this costs to make" panel, shared with the To make
  // queue rather than reimplemented here.
  const [costPanel, setCostPanel] = useState(false);
  // #515 — recording a run from the item page, the doorway that does not
  // depend on anything being low. See the button's own note.
  const [recordRun, setRecordRun] = useState(false);
  // #761, Session 79.22b.6c.3 -- Record production's answer and its Undo, here
  // as on To make (T4, reused unchanged: 682 D14). It used to say nothing.
  const [shelfRunNote, setShelfRunNote] = useState(null);
  const [shelfRunBusy, setShelfRunBusy] = useState(false);
  // #761 / #702, T5 -- Correct on a Made line: what the database's test needs for
  // the lines shown (null when a read failed - then no Correct), and the run
  // being corrected.
  const [shelfFacts, setShelfFacts] = useState(null);
  const [fixingRun, setFixingRun] = useState(null);
  const { coarse } = useTier();

  // Does ANY branch make this? Not "does mine" — a Dallas manager looking at a
  // stake should see what it costs to make, because that is the number the
  // stakes on their shelf were valued at when Houston sent them.
  //
  // DECLARED HERE, BELOW THE STATE, AND IT MUST STAY BELOW IT. This first sat
  // beside the permission predicates twenty lines up, where `categories` is in
  // its temporal dead zone — a `const` is hoisted but not initialised, so
  // reading it threw and THE WHOLE ITEM PAGE RENDERED BLANK. All 1,403 tests
  // passed throughout, because no test imports this page. A build that compiles
  // is not a page that renders, and the only instrument that sees the
  // difference is loading the screen.
  const isMadeSomewhere = resolveFulfilment(categories, item?.category_id).mode === 'make';

  // Whether the person reading this is standing at the branch that makes it.
  // A narrower question than the one above, and it gates the ACTION rather
  // than the figure: everyone who can see cost may see what a stake costs to
  // make; only Houston is offered a button to record making one.
  //
  // `profile?.branch_id` rather than the `userBranchId` const, which is
  // declared SIXTY LINES BELOW THIS. That is the same temporal-dead-zone trap
  // that blanked this whole page earlier in this session — caught here by
  // grepping for the declaration before loading the screen rather than after.
  const myBranchMakesThis = branchMakes(categories, item, profile?.branch_id);

  // #684, Session 79.22b.4 -- WHERE A REQUEST FROM THIS PAGE GOES. All three of
  // its request buttons asked Purchasing for anything, a Houston frame
  // included; this page read the category tree for the cost strip and Record
  // production only. The same rule every other door reads, so the phone
  // buttons say "Ask Houston" and the desk card leads with the route.
  // Declared here, below `categories`, for the reason the comment above gives.
  const requestRt = requestRoute({ categories, categoryId: item?.category_id, viewerBranchId: profile?.branch_id ?? null });
  const requestMaker = requestRt.makerBranchId == null ? null
    : (BRANCHES.find(b => String(b.id) === String(requestRt.makerBranchId))?.name || 'that branch');
  const [vendors, setVendors] = useState([]);
  const [txCount, setTxCount] = useState(0);
  // #343, Session 78m. How many receipts of this item are still carrying a
  // GUESS at what they cost -- booked at receive, never settled by an invoice.
  // Null until counted, so the strip can stay silent rather than flash a zero.
  const [provisionalReceipts, setProvisionalReceipts] = useState(null);
  // #656. How this item got here AT THIS BRANCH: bought from a vendor, or
  // moved in from another branch. Null until counted -- see CostBasisStrip.
  const [vendorReceipts, setVendorReceipts] = useState(null);
  const [transfersIn, setTransfersIn] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [reorderEdits, setReorderEdits] = useState({});
  // #512 -- the open transfers carrying this item, read once per load.
  const [incoming, setIncoming] = useState([]);
  const [incomingFailed, setIncomingFailed] = useState(false);
  const [savingReorder, setSavingReorder] = useState(false);
  const [reorderSaved, setReorderSaved] = useState(false);

  const [uploadingImage, setUploadingImage] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [showAddItem, setShowAddItem] = useState(false);
  const [showEditSpecs, setShowEditSpecs] = useState(false);

  const [restockQty, setRestockQty] = useState(1);
  const [restockJobRef, setRestockJobRef] = useState('');
  const [restockSaving, setRestockSaving] = useState(false);
  const [restockDone, setRestockDone] = useState(false);
  const [restockError, setRestockError] = useState('');
  // #684 G -- the desk card's purchase form, opened on purpose for a made item.
  const [restockBuyInstead, setRestockBuyInstead] = useState(false);

  const [showReceiveSheet, setShowReceiveSheet] = useState(false);
  const [receiveMatchedRequest, setReceiveMatchedRequest] = useState(null);
  // #279. null until a matched receive returns. Carries the database's own
  // all_complete so the done screen can say whether the order actually closed
  // instead of asserting that it did.
  const [receiveOrderClosed, setReceiveOrderClosed] = useState(null);
  // #497 - the receive saved but its packing slip photo did not upload.
  const [receivePhotoFailed, setReceivePhotoFailed] = useState(false);
  const [receiveSaving, setReceiveSaving] = useState(false);
  const [receiveDone, setReceiveDone] = useState(false);
  const [receiveError, setReceiveError] = useState('');

  const [showUseSheet, setShowUseSheet] = useState(false);
  const [useSaving, setUseSaving] = useState(false);
  const [useDone, setUseDone] = useState(false);
  const [useError, setUseError] = useState('');

  // #364 — Adjust now opens in place on both surfaces, like Receive and Use.
  // adjustDoneReason is kept separately because the sheet resets its own form
  // state on submit, and the done panel still has to know whether this was an
  // Opening count in order not to claim a notification that #369 suppressed.
  const [showAdjustSheet, setShowAdjustSheet] = useState(false);
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [adjustDone, setAdjustDone] = useState(false);
  const [adjustError, setAdjustError] = useState('');
  const [adjustDoneReason, setAdjustDoneReason] = useState(null);
  // #363 D17 -- what the save did, so the done screen says it.
  const [adjustDoneText, setAdjustDoneText] = useState('');
  const [receivePrintOption, setReceivePrintOption] = useState(null);
  const [receivePrintDescription, setReceivePrintDescription] = useState('Checking printer...');
  // #333. Pack size for the label-count default. Seeded to 1 rather than null so
  // the sheet renders "one label per unit" — today's behaviour — while the
  // lookup is in flight, instead of flashing a count it will immediately change.
  const [receivePack, setReceivePack] = useState({ packQty: 1, packUnit: null, vendorName: null });

  const imageFileRef = useRef();

  const userBranchId = profile?.branch_id;
  const branchName = BRANCHES.find(b => b.id === userBranchId)?.name || 'your branch';

  // #761, T4 on the item's page -- the answer sits at the top of the movement
  // list with its Undo until the next press (F1, F3). A cost change alone is
  // not a run and answers nothing new.
  function answerShelfRun(r) {
    if (r && !r.costOnly && r.txId != null) {
      setShelfRunNote({
        kind: 'done', txId: r.txId,
        text: recordedLine({ qty: r.qty, itemName: item?.name, branchName, qtyOnHand: r.qtyOnHand }),
      });
    }
    load();
  }
  async function undoShelfRun(note) {
    setShelfRunBusy(true);
    const r = await undoRecordProduction(note?.txId);
    setShelfRunNote(r.error
      ? { ...note, kind: 'error', text: plainTransferRefusal(r.error.message).text }
      : { ...note, kind: 'undone' });
    setShelfRunBusy(false);
    load();
  }
  // #761 / #702, T5 -- CORRECT ON A MADE LINE. The whole auth goes to the rule
  // (locked rule 8); the database's own test decides which lines qualify
  // (shelfRunCorrectable(), corrections.js). Saving answers at the top of the list
  // the way Record production does, without an Undo (P3's answer has none).
  const correctAuth = { isAdmin, isManager, isExecutive, isPurchasing, isTeamMember, profile };
  const anyFixable = transactions.some(tx => shelfRunCorrectable(correctAuth, tx, shelfFacts));
  const branchNamesById = Object.fromEntries(BRANCHES.map(b => [String(b.id), b.name]));
  const correctionSheet = fixingRun ? (
    <CorrectionSheet
      kind="shelf_run"
      context={{
        branchId: fixingRun.branch_id,
        onHand: stockRows.find(r => String(r.branch_id) === String(fixingRun.branch_id))?.qty_on_hand ?? null,
      }}
      items={[{
        id: fixingRun.id, qty: fixingRun.qty, name: item?.name || '', itemId: item?.id ?? null,
        recordedByName: shelfFacts?.names?.[String(fixingRun.user_id)] || null,
        at: fixingRun.created_at ? new Date(fixingRun.created_at) : null,
      }]}
      branchNames={branchNamesById}
      onClose={() => setFixingRun(null)}
      onRaced={() => { load(); }}
      onSaved={async (answer) => {
        await load();
        setFixingRun(null);
        setShelfRunNote({ kind: 'corrected', text: answer?.[0] || '' });
      }}
    />
  ) : null;

  // THE NEXT PRESS ends the answer and its Undo: any button, link or field on
  // the page (a portal's too - React bubbles through it), but not the answer's
  // own Undo.
  function endShelfRunOnPress(e) {
    if (!shelfRunNote || !e.target?.closest) return;
    if (e.target.closest('[data-part="shelf-run-answer"]')) return;
    if (e.target.closest('button, a, [role="button"], input, select, textarea')) setShelfRunNote(null);
  }

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setError('');
    try {
      // #626, Session 79.22b.1 -- `standard_cost_set_by` WAS MISSING FROM THIS
      // SELECT AND NOTHING FAILED, WHICH IS WHY NOBODY SAW IT.
      //
      // An executive sets what a stake costs to make, and Colby's 79.8 ruling is
      // that a branch manager may not then override it. This page showed her NO
      // LOCK: the box was live, she typed a new figure, the screen worked out the
      // revaluation for her - "923 on hand goes from $4,845.75 to $5,076.50" -
      // and the save button was green. The database refused it only after she
      // pressed, so the work she had just done could never have landed. The rule
      // held; the screen could not say so, because the one column naming the
      // setter was never fetched.
      //
      // It also made the provenance line lie on every item whose cost has ever
      // been set: "not set by anyone yet, it came across from the old vendor
      // price", about a figure an executive chose.
      //
      // THE ABSENCE OF A LOCK LOOKS EXACTLY LIKE AN UNLOCKED COST, which is why
      // no test could see it, and why CLAUDE.md's rule exists: after adding a
      // column, grep every select() on that table. production.js selects it
      // correctly, which is what kept this invisible.
      const { data: itemData, error: itemErr } = await supabase
        .from('inventory_items')
        .select(`
          id, name, sku, uom, description, image_url, is_active, category_id, vendor_id,
          standard_cost, standard_cost_set_at, standard_cost_set_by,
          inventory_categories ( id, name, parent_id ),
          inventory_vendors ( id, name )
        `)
        .eq('id', itemId)
        .single();
      if (itemErr) throw itemErr;
      setItem(itemData);

      const { data: stock } = await supabase
        .from('inventory_stock')
        .select('id, branch_id, qty_on_hand, reorder_point, reorder_qty, location_note, avg_unit_cost')
        .eq('item_id', itemId);
      setStockRows(stock || []);

      const edits = {};
      (stock || []).forEach(s => {
        edits[s.branch_id] = {
          reorder_point: s.reorder_point ?? '',
          reorder_qty:   s.reorder_qty   ?? '',
        };
      });
      setReorderEdits(edits);

      // #512 -- WHAT IS ON ITS WAY, read EXACTLY as Stock Home reads it for all
      // branches: the work in hand (transfersInHand(), one rule in the
      // database), the same statuses, the same columns. onItsWayTo() does the
      // arithmetic and is keyed by item, so this page and Stock Home cannot
      // disagree. NOT narrowed to this item at the database: a filter on the
      // lines of an RPC's result is refused ("column pgrst_call.item_id does not
      // exist", tried at 79.22b.6.1f), and the work in hand is bounded anyway --
      // cutOff() below says so if it ever is not. Its own try: a failed check is
      // SAID on the card (B4) and never takes the rest of the page down.
      try {
        const read = await transfersInHand(`
            id, transfer_number, status, from_branch_id, to_branch_id, fulfilment_mode,
            confirmed_date, requested_at,
            branch_transfer_items ( item_id, qty_requested, qty_ready, qty_shipped, qty_received, line_status, promised_date )
          `)
          .in('status', ON_ITS_WAY_STATUSES);
        // A list cut at the database's row limit is a failed check, never a
        // smaller answer (#608 / gate 675 D17).
        const bad = read.error || cutOff(read);
        setIncomingFailed(!!bad);
        setIncoming(bad ? [] : (read.data || []));
      } catch (e) {
        setIncomingFailed(true);
        setIncoming([]);
      }

      const { data: txData, count } = await supabase
        .from('inventory_transactions')
        // #761, T5 -- item_id, user_id, created_at, qty, transfer_id and correction_id
        // are the facts the Made line's Correct is decided by (shelfRunCorrectable()).
        .select('id, item_id, tx_type, qty, qty_after, work_order_ref, user_id, created_at, branch_id, transfer_id, reverses_tx_id, correction_id', { count: 'exact' })
        .eq('item_id', itemId)
        .order('created_at', { ascending: false })
        .limit(10);
      setTransactions(txData || []);
      // #761, T5 -- which Made lines are runs for the shelf, still live, and whose.
      const facts = await fetchShelfRunFacts(txData || []);
      setShelfFacts(facts.data);
      setTxCount(count || 0);

      // #343 / V11. Counted across ALL receipts of this item, not just the ten
      // shown above -- a provisional count that only reflected the visible page
      // would understate exactly when it mattered most.
      //
      // head: true fetches no rows, only the count. Branch scoping is left to
      // RLS on inventory_transactions, which already limits a manager to their
      // own branch and lets admin and executive see all four -- so this figure
      // matches what the same person would count by hand in the Ledger.
      const { count: provCount } = await supabase
        .from('inventory_transactions')
        .select('id', { count: 'exact', head: true })
        .eq('item_id', itemId)
        .eq('tx_type', 'receive')
        .is('unit_cost_settled', null)
        .not('unit_cost_at_transaction', 'is', null);
      setProvisionalReceipts(provCount ?? 0);

      // #656 -- SCOPED TO THE BRANCH BEING READ, deliberately, and unlike the
      // count above. The strip shows the viewer's OWN branch average, so the
      // sentence under it has to describe that branch: Houston may have bought
      // an item Dallas has only ever received by transfer, and a company-wide
      // count would tell Dallas an invoice settled a figure Dallas's stock
      // never had.
      //
      // `transfer_in` rather than `receive` is what tells them apart:
      // count_in_load() writes `transfer_in`, so `receive` means a vendor
      // delivery and nothing else. Read in the migration rather than assumed.
      const branchForCounts = profile?.branch_id ?? null;
      if (branchForCounts != null) {
        const [{ count: vendorCount }, { count: transferCount }] = await Promise.all([
          supabase.from('inventory_transactions')
            .select('id', { count: 'exact', head: true })
            .eq('item_id', itemId).eq('branch_id', branchForCounts).eq('tx_type', 'receive'),
          supabase.from('inventory_transactions')
            .select('id', { count: 'exact', head: true })
            .eq('item_id', itemId).eq('branch_id', branchForCounts).eq('tx_type', 'transfer_in'),
        ]);
        setVendorReceipts(vendorCount ?? 0);
        setTransfersIn(transferCount ?? 0);
      } else {
        // No branch to describe, so the strip keeps its original sentence
        // rather than making a claim about a branch nobody is standing at.
        setVendorReceipts(null);
        setTransfersIn(null);
      }


      // `code` feeds #471's SKU proposal in AddItemModal; manufactured_at_branch_id
      // feeds #515's cost-to-make strip, which must not render on something D&R
      // BUYS. Session 79.5 disproved reading the vendor link for that and the
      // tree is the authority — Dibond carries a Houston vendor row and Houston
      // makes none of it.
      const { data: cats } = await supabase.from('inventory_categories').select('id, name, parent_id, is_active, code, manufactured_at_branch_id').order('name');
      setCategories(cats || []);

      const { data: vens } = await supabase.from('inventory_vendors').select('id, name').eq('is_active', true).order('name');
      setVendors(vens || []);

    } catch (e) {
      setError(e.message || 'Failed to load item.');
    } finally {
      setLoading(false);
    }
  }, [itemId, userBranchId]);

  useEffect(() => { load(); }, [load]);

  // #512 -- the sentences for one branch's row, from the shared derivation and
  // in Stock Home's own words. "here" for the viewer's branch, the branch's name
  // for any other, exactly as Stock Home says it when looking at another branch.
  function comingLinesFor(branchId) {
    const entries = (onItsWayTo(incoming, branchId) || {})[item?.id] || [];
    const elsewhereName = String(userBranchId) === String(branchId) ? null : BRANCH_NAMES[String(branchId)];
    return entries.map(e => onItsWayLine(e, { branchNames: BRANCH_NAMES, elsewhereName })).filter(Boolean);
  }

  // ── REORDER SETTINGS SAVE ─────────────────────────────────────────────────

  async function handleSaveReorder() {
    setSavingReorder(true);
    try {
      const updates = stockRows
        .filter(s => {
          if (isAdmin || isExecutive) return true;
          if (isBranchScoped) return s.branch_id === userBranchId;
          return false;
        })
        .map(s => {
          const edits = reorderEdits[s.branch_id] || {};
          return supabase
            .from('inventory_stock')
            .update({
              reorder_point: edits.reorder_point !== '' ? parseInt(edits.reorder_point) : null,
              reorder_qty:   edits.reorder_qty   !== '' ? parseInt(edits.reorder_qty)   : null,
            })
            .eq('id', s.id);
        });
      await Promise.all(updates);
      setReorderSaved(true);
      setTimeout(() => setReorderSaved(false), 2500);
      await load();
    } catch (e) {
      setError('Failed to save reorder settings.');
    } finally {
      setSavingReorder(false);
    }
  }

  // ── IMAGE UPLOAD ──────────────────────────────────────────────────────────

  async function handleImageUpload(e) {
    const file = e.target.files[0];
    if (!file || !item) return;
    setUploadingImage(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `items/${item.id}.${ext}`;
      const { error: upErr } = await supabase.storage.from('inventory-assets').upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('inventory-assets').getPublicUrl(path);
      await supabase.from('inventory_items').update({ image_url: urlData.publicUrl }).eq('id', item.id);
      await load();
    } catch (e) {
      setError('Image upload failed.');
    } finally {
      setUploadingImage(false);
    }
  }


  // ── OPEN RECEIVE SHEET ────────────────────────────────────────────────────

  function openReceiveSheet() {
    setReceiveDone(false);
    setReceiveError('');
    setReceivePrintOption(null);
    setReceivePrintDescription('Checking printer...');
    setReceivePack({ packQty: 1, packUnit: null, vendorName: null });
    resolveReceivePrintOption(branchName).then(({ option, description }) => {
      setReceivePrintOption(option);
      setReceivePrintDescription(description);
    });
    // receiveMatchedRequest kept in sync via onRequestsChanged on MaterialRequestsPanel
    setShowReceiveSheet(true);
  }

  // #333. The pack lookup lives in an effect rather than inside openReceiveSheet
  // because on this page the matched request is NOT known at open time — it is
  // synced in afterwards by MaterialRequestsPanel's onRequestsChanged. Reading
  // the vendor at open would therefore read null on the exact path that has a
  // vendor, which is the goods-receipt path Dallas uses most. Keying the effect
  // on vendor_id re-reads the pack size the moment the matched order lands.
  useEffect(() => {
    if (!showReceiveSheet || !itemId) return;
    let cancelled = false;
    fetchPackSnapshot(itemId, receiveMatchedRequest?.vendor_id || null).then(pack => {
      if (!cancelled) setReceivePack(pack);
    });
    return () => { cancelled = true; };
  }, [showReceiveSheet, itemId, receiveMatchedRequest?.vendor_id]);

  // ── SUBMIT RECEIVE ────────────────────────────────────────────────────────

  async function submitReceive({ qty, jobRef, notes, packingSlip, matchedRequest, printLabels, labelCopies }) {
    if (!item || !profile) return;
    setReceiveSaving(true);
    setReceiveError('');
    setReceiveOrderClosed(null);
    setReceivePhotoFailed(false);
    try {
      const result = await submitReceiveStock({
        item: { id: itemId, name: item.name, sku: item.sku, uom: item.uom },
        branchId: userBranchId,
        branchName,
        qty,
        jobRef,
        notes,
        packingSlip,
        matchedRequest,
        printLabels,
        printOption: receivePrintOption,
        labelCopies,
        profile,
      });

      // #279. Report what the receive did to the order rather than assuming it
      // closed it — a partial receive now leaves the request open.
      setReceiveOrderClosed(matchedRequest ? result?.allComplete !== false : null);
      // #497 — the receive succeeded; the photo may not have. Said on the success
      // screen rather than the whole receive being discarded.
      setReceivePhotoFailed(result?.photoFailed === true);
      setReceiveDone(true);
      await load();
    } catch (e) {
      setReceiveError(e.message);
    } finally {
      setReceiveSaving(false);
    }
  }

  // ── OPEN USE SHEET ────────────────────────────────────────────────────────

  function openUseSheet() {
    setUseDone(false);
    setUseError('');
    setShowUseSheet(true);
  }

  // ── FETCH JOB HISTORY ─────────────────────────────────────────────────────
  // Passed down to UseSheetContent as a prop; returns the result rather than
  // setting state here, since the sheet owns its own jobHistory state now.

  async function fetchJobHistory(jobRef) {
    if (!jobRef || !itemId) return null;
    try {
      const { data } = await supabase
        .from('inventory_transactions')
        .select('id, qty, created_at')
        .eq('item_id', itemId)
        .eq('tx_type', 'consume')
        .eq('work_order_ref', jobRef);

      if (data && data.length > 0) {
        const totalUsed = data.reduce((sum, tx) => sum + Math.abs(tx.qty), 0);
        const lastDate = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0].created_at;
        return { totalUsed, txCount: data.length, lastDate };
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  // ── SUBMIT USE ────────────────────────────────────────────────────────────

  // #349, Session 79.22b.6.1g -- the save, its checks, the stock record (#789)
  // and the alert live in src/lib/stockMovements.js, shared with the Use screen.
  // It reads the count fresh rather than trusting the figure this page loaded,
  // and saves the note where Receive does (#788).
  async function submitUse({ qty, jobRef, notes }) {
    if (!item || !profile) return;
    setUseSaving(true);
    setUseError('');
    try {
      const result = await submitUseStock({
        item: { id: itemId, name: item.name },
        branchId: userBranchId,
        branchName: BRANCHES.find(b => b.id === userBranchId)?.name || '',
        qty,
        jobRef,
        notes,
        profile,
        requireJobRef: requireJobRefOnUse,
      });
      if (result.refused) { setUseError(result.refused); return; }

      setUseDone(true);
      await load();
    } catch (e) {
      setUseError('Something went wrong: ' + e.message);
    } finally {
      setUseSaving(false);
    }
  }

  // ── SUBMIT ADJUST ─────────────────────────────────────────────────────────
  // #364 gave this page its own Adjust, copied from the Use screen's on purpose
  // until #349 lifted the save into src/lib/. It has (Session 79.22b.6.1g): both
  // screens now save through submitAdjustStock(), which checks the role inside
  // the save as well as here (#374), reads the count fresh, creates a missing
  // stock record (#789) and keeps #369's Opening count exception.

  async function submitAdjust({ newQty, reason, notes }) {
    if (!item || !profile) return;
    setAdjustSaving(true);
    setAdjustError('');
    try {
      const result = await submitAdjustStock({
        auth: { isAdmin, isManager, isExecutive, isTeamMember, isPurchasing, profile },
        item: { id: itemId, name: item.name },
        branchId: userBranchId,
        branchName: BRANCHES.find(b => b.id === userBranchId)?.name || '',
        newQty,
        reason,
        notes,
        profile,
      });
      if (result.refused) { setAdjustError(result.refused); return; }

      setAdjustDoneReason(reason);
      setAdjustDoneText(adjustDoneLine(result));
      setAdjustDone(true);
      await load();
    } catch (e) {
      setAdjustError('Something went wrong: ' + e.message);
    } finally {
      setAdjustSaving(false);
    }
  }

  function openAdjustSheet() {
    setAdjustDone(false);
    setAdjustError('');
    setAdjustDoneReason(null);
    setAdjustDoneText('');
    setShowAdjustSheet(true);
  }

  // ── DEACTIVATE ────────────────────────────────────────────────────────────

  async function handleDeactivate() {
    setDeactivating(true);
    try {
      await supabase.from('inventory_items').update({ is_active: false }).eq('id', item.id);
      await load();
    } catch (e) {
      setError('Failed to deactivate item.');
    } finally {
      setDeactivating(false);
    }
  }

  async function handleReactivate() {
    try {
      await supabase.from('inventory_items').update({ is_active: true }).eq('id', item.id);
      await load();
    } catch (e) {
      setError('Failed to reactivate item.');
    }
  }

  // ── DELETE ────────────────────────────────────────────────────────────────

  async function handleDelete() {
    if (txCount > 0) return;
    setDeleting(true);
    try {
      await supabase.from('inventory_stock').delete().eq('item_id', item.id);
      await supabase.from('inventory_items').delete().eq('id', item.id);
      setActive('inv_catalog');
    } catch (e) {
      setError('Failed to delete item.');
      setDeleting(false);
    }
  }

  // ── SUBMIT RESTOCK REQUEST ────────────────────────────────────────────────

  function openRestockForm() {
    setScanItemId(item.id);
    setActive('inv_request');
  }

  async function submitRestockRequest() {
    if (!item || !profile) return;
    if (!restockQty || restockQty < 1) { setRestockError('Please enter a valid quantity.'); return; }
    setRestockSaving(true);
    setRestockError('');
    try {
      const branchName = BRANCHES.find(b => b.id === userBranchId)?.name || '';
      await createRestockRequest({
        itemId: item.id,
        itemName: item.name,
        itemUom: item.uom,
        branchId: userBranchId,
        branchName,
        qty: restockQty,
        workOrderRef: restockJobRef || null,
        notes: null,
        requestedBy: { id: profile.id, full_name: profile.full_name },
      });
      setRestockDone(true);
      await load();
    } catch (err) {
      setRestockError('Something went wrong: ' + err.message);
    } finally {
      setRestockSaving(false);
    }
  }

  // ── REORDER EDIT HELPER ───────────────────────────────────────────────────

  function isEditable(branchId) {
    if (isAdmin || isExecutive) return true;
    if (isBranchScoped) return branchId === userBranchId;
    return false;
  }

  // ── LOADING / ERROR ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="loading" style={{ padding: 48, textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto 8px' }} />
        Loading item...
      </div>
    );
  }

  if (!item) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-lt)' }}>
        <IconPackage size={32} style={{ marginBottom: 8 }} />
        <div>Item not found.</div>
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => setActive('inv_catalog')}>
          Back to catalog
        </button>
      </div>
    );
  }

  const cat = item.inventory_categories;
  // 74s. Parent resolved from the category list this page already fetches, not from the
  // nested self-embed - see src/lib/categoryLabel.js for why that embed never returned a
  // parent. Category rendered as an em dash on every item carrying a subcategory.
  const { parent: catParent, sub: catSub } = categoryLabel(cat, categories);

  const homeStockQty = stockRows.find(s => s.branch_id === userBranchId)?.qty_on_hand ?? 0;

  // ── MOBILE ────────────────────────────────────────────────────────────────

  if (isMobile) {
    const homeStock = stockRows.find(s => s.branch_id === userBranchId);
    const status = homeStock ? getStockStatus(homeStock.qty_on_hand, homeStock.reorder_point) : null;
    const s = status ? STATUS_STYLES[status] : null;
    const isNegative = (homeStock?.qty_on_hand ?? 0) < 0;

    return (
      <div style={{ background: '#F5F5F3', minHeight: '100vh', paddingBottom: 80 }} onClickCapture={endShelfRunOnPress}>
        <div style={{ background: 'white', padding: '10px 14px 12px', borderBottom: '1px solid #E8E8E8', position: 'sticky', top: 'var(--topbar-height)', zIndex: 10 }}>
          <button onClick={() => setActive('inv_catalog')}
            style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#5C5C5C', fontSize: 12, cursor: 'pointer', padding: 0, marginBottom: 4 }}>
            <IconArrowLeft size={13} /> Catalog
          </button>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0F0F0F' }}>{item.name}</div>
          <div style={{ fontSize: 11, color: '#7A7A7A' }}>
            SKU: {item.sku || '—'} · {item.uom}
            {!item.is_active && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 5, fontSize: 9, background: '#F0F0F0', color: '#9A9A9A' }}>Inactive</span>}
            {isNegative && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 5, fontSize: 9, background: '#FCEBEB', color: '#A32D2D', fontWeight: 700 }}>Audit needed</span>}
          </div>
        </div>

        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>

          {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>{error}</div>}

          {item.image_url && (
            <StoredImage value={item.image_url} bucket={INVENTORY_BUCKET} alt={item.name} style={{ width: '100%', borderRadius: 10, border: '1px solid #E8E8E8', objectFit: 'cover', maxHeight: 200 }} />
          )}

          {/* QUICK ACTIONS */}
{/* #364 — column count follows the button count so hiding Adjust does not
    leave a hole in the row. */}
<div style={{ display: 'grid', gridTemplateColumns: `repeat(${canAdjust ? 4 : 3}, 1fr)`, gap: 6 }}>
  <button onClick={openReceiveSheet}
    style={{ padding: '10px 0', background: '#1D9E75', color: 'white', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
    + Receive
  </button>
  <button onClick={openUseSheet}
    style={{ padding: '10px 0', background: '#0F0F0F', color: 'white', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
    − Use
  </button>
  {/* #364 — was setActive('inv_adjust'), which navigated off this page and lost
      the item you were looking at. Receive and Use both open in place; Adjust
      was the only one that did not. Hidden rather than disabled when the user
      cannot adjust, matching how the desktop Stock Actions row behaves. */}
  {canAdjust && (
    <button onClick={openAdjustSheet}
      style={{ padding: '10px 0', background: 'white', color: '#0F0F0F', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
      Adjust
    </button>
  )}
  {/* #515, Session 79.8 — THE SECOND DOORWAY INTO RECORDING A PRODUCTION RUN.

      Colby, looking at the built To make queue: "houston will need a way to
      record production that's not tied to any specific transfer request." The
      first build had ONE way in, a reserve row, and a reserve row needs the
      item to be at or below its reorder point — which no Houston item is. So
      the button lived on a row that never rendered and Houston could record
      nothing at all.

      WITHOUT THIS, PEOPLE USE ADJUST, which sits right beside it. That writes
      a stock correction with no cost and no production against it — precisely
      the record #515 exists to stop, and it looks correct to everybody.

      Shown only where the CATEGORY TREE says this branch makes the thing. The
      database is deliberately looser — a branch that cuts something unusual
      once should be able to say so — and the shipped function says why: "the
      screen only offers this where it makes sense; the database does not
      pretend to know." Offering it on every stake at Dallas is the undesigned
      permission gate D35 warns about. */}
  {myBranchMakesThis && (
    <button onClick={() => setRecordRun(true)}
      style={{ padding: '10px 0', background: 'white', color: '#0F0F0F', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
      Record production
    </button>
  )}
  {/* #684 -- the label says where the request goes; the request screen routes
      it (Ask the maker, or Make these ourselves at the maker). */}
  <button onClick={openRestockForm}
    style={{ padding: '10px 0', background: 'white', color: '#0F0F0F', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
    {requestRt.route === 'ask_maker' ? `Ask ${requestMaker}` : requestRt.route === 'make_own' ? 'Make these' : 'Restock'}
  </button>
</div>

{/* #515 D32. What it costs US TO MAKE, which is a different number from the
    cost basis below it and must never be shown as one. The strip renders only
    on something a branch actually manufactures — the category tree decides,
    never the vendor link (Session 79.5). */}
{canSeeCost && isMadeSomewhere && (
  <div style={{ marginBottom: 10 }}>
    <CostToMakeStrip
      item={item}
      canChange={maySetProductionCost}
      onChange={() => setCostPanel(true)}
      compact
    />
  </div>
)}

{canSeeCost && (
  <div style={{ marginBottom: 10 }}>
    <CostBasisStrip
      avgCost={stockRows.find(r => r.branch_id === userBranchId)?.avg_unit_cost ?? null}
      provisionalCount={provisionalReceipts || 0}
      vendorReceipts={vendorReceipts}
      transfersIn={transfersIn}
      compact
    />
  </div>
)}

{/* STOCK · ALL BRANCHES */}
<div style={{ background: 'white', borderRadius: 10, border: `1px solid ${isNegative ? '#F09595' : '#E8E8E8'}`, overflow: 'hidden' }}>
  <div style={{ padding: '8px 12px 6px', borderBottom: '1px solid #F0F0F0' }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '.7px' }}>Stock · all branches</div>
  </div>
  {BRANCHES.map((branch, idx) => {
    const bs = stockRows.find(r => r.branch_id === branch.id);
    const qty = bs?.qty_on_hand ?? 0;
    const isNeg = qty < 0;
    const bStatus = bs ? getStockStatus(qty, bs.reorder_point) : null;
    const bSt = bStatus ? STATUS_STYLES[bStatus] : null;
    const isHome = branch.id === userBranchId;
    const dotColor = isNeg ? '#A32D2D' : bSt ? bSt.dot : '#D0D0D0';
    // #512 -- drawing B, phone: the row is unchanged and what is coming sits
    // under it, indented to the branch name. The badge still says what is on
    // the shelf now (B2).
    const coming = comingLinesFor(branch.id);
    return (
      <div key={branch.id} style={{ padding: '8px 12px', borderBottom: idx < 3 ? '1px solid #F0F0F0' : 'none', background: isHome ? '#F8FFFB' : 'white' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, display: 'inline-block', marginRight: 8, flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: isHome ? 700 : 400, color: '#0F0F0F', flex: 1 }}>
          {branch.name}
          {isHome && <span style={{ marginLeft: 5, fontSize: 9, color: '#1D9E75', fontWeight: 700 }}>your branch</span>}
        </span>
        {bs?.location_note && (
          <span style={{ fontSize: 10, color: '#9A9A9A', marginRight: 8 }}>📍 {bs.location_note}</span>
        )}
        <span style={{ fontSize: 15, fontWeight: 700, color: isNeg ? '#A32D2D' : bSt ? bSt.color : '#9A9A9A', minWidth: 28, textAlign: 'right', marginRight: 8 }}>
          {bs ? qty : '—'}
        </span>
        {bStatus && (
          <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 10, fontSize: 9, fontWeight: 700, background: bSt.bg || bSt.color + '22', color: bSt.color, minWidth: 30, textAlign: 'center' }}>
            {bStatus}
          </span>
        )}
        {!bStatus && bs && <span style={{ fontSize: 9, color: '#C0C0C0', minWidth: 30, textAlign: 'center' }}>—</span>}
      </div>
      {coming.length > 0 && (
        <div style={{ margin: '4px 0 0 16px' }}>
          {coming.map((l, i) => <div key={i} style={COMING_LINE}>{l}</div>)}
        </div>
      )}
      </div>
    );
  })}
  {incomingFailed && (
    <div role="status" style={{ padding: '6px 12px', borderTop: '1px solid #F0F0F0', fontSize: 11, color: '#8A5A08' }}>{INCOMING_FAILED}</div>
  )}
  {homeStock?.reorder_point != null && (
    <div style={{ padding: '6px 12px', background: '#F8F8F8', borderTop: '1px solid #F0F0F0' }}>
      <span style={{ fontSize: 10, color: '#9A9A9A' }}>Reorder at {homeStock.reorder_point} · qty {homeStock.reorder_qty ?? '—'}</span>
    </div>
  )}
</div>
<div style={{ background: 'white', borderRadius: 10, border: '1px solid #E8E8E8', padding: '10px 12px' }}>
  <VendorItemsPanel
    itemId={item.id}
    itemName={item.name}
    itemUom={item.uom}
    isMobile={true}
    setActive={setActive}
    setFilterItemId={setFilterItemId}
    setFilterItemName={setFilterItemName}
  />
</div>

          {/* LABEL */}
          <LabelCard item={item} branchName={branchName} isMobile={true} />
          {/* RESTOCK CARD */}
          <MaterialRequestsPanel
            itemId={itemId}
            itemName={item?.name || ''}
            branchId={userBranchId}
            compact={true}
            canManage={canManageRequests}
            setActive={setActive}
            setOpenRequestId={setOpenRequestId}
            onRequestsChanged={updated => {
              const orderedReq = updated.find(r => r.status === 'ordered') || null;
              setReceiveMatchedRequest(orderedReq);
            }}
          />
          <button onClick={openRestockForm}
            style={{ width: '100%', padding: '10px 0', background: '#0F0F0F', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', marginTop: 6 }}>
            {requestRt.route === 'ask_maker' ? `+ Ask ${requestMaker}` : requestRt.route === 'make_own' ? '+ Make these' : '+ New request'}
          </button>

          {/* RECEIVE BOTTOM SHEET */}
          {showReceiveSheet && (
            <Overlay variant="sheet" onClose={() => setShowReceiveSheet(false)}>
              <div style={{ padding: '20px 20px 40px' }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '0 auto 16px' }} />
                <ReceiveSheetContent
                  itemName={item.name}
                  itemId={item.id ?? null}
                  itemUom={item.uom || null}
                  branchName={branchName}
                  matchedRequest={receiveMatchedRequest}
                  canOverride={canOverrideJobRef}
                  printDescription={receivePrintDescription}
                  printAvailable={!!receivePrintOption}
                  packQty={receivePack.packQty}
                  packUnit={receivePack.packUnit}
                  packVendorName={receivePack.vendorName}
                  onSubmit={submitReceive}
                  onCancel={() => setShowReceiveSheet(false)}
                  saving={receiveSaving}
                  done={receiveDone}
                  orderClosed={receiveOrderClosed}
                  photoFailed={receivePhotoFailed}
                  serverError={receiveError}
                />
              </div>
            </Overlay>
          )}

          {/* USE BOTTOM SHEET */}
          {showUseSheet && (
            <Overlay variant="sheet" onClose={() => setShowUseSheet(false)}>
              <div style={{ padding: '20px 20px 40px' }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '0 auto 16px' }} />
                <UseSheetContent
                  itemName={item.name}
                  branchName={branchName}
                  homeStockQty={homeStockQty}
                  itemUom={item.uom}
                  onFetchJobHistory={fetchJobHistory}
                  onSubmit={submitUse}
                  onCancel={() => setShowUseSheet(false)}
                  saving={useSaving}
                  done={useDone}
                  serverError={useError}
                  requireJobRef={requireJobRefOnUse}
                />
              </div>
            </Overlay>
          )}

          {/* ADJUST BOTTOM SHEET — #364. Same Overlay sheet variant as Receive
              and Use, so Adjust keeps your place on the page like they do. */}
          {showAdjustSheet && (
            <Overlay variant="sheet" onClose={() => setShowAdjustSheet(false)}>
              <div style={{ padding: '20px 20px 40px' }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '0 auto 16px' }} />
                <AdjustSheetContent
                  itemName={item.name}
                  branchName={branchName}
                  currentQty={homeStockQty}
                  itemUom={item.uom}
                  userName={profile?.full_name}
                  onSubmit={submitAdjust}
                  onCancel={() => setShowAdjustSheet(false)}
                  saving={adjustSaving}
                  done={adjustDone}
                  doneReason={adjustDoneReason}
                  doneLine={adjustDoneText}
                  serverError={adjustError}
                  itemId={itemId}
                  branchId={userBranchId}
                  canSeeCost={canSeeCost}
                />
              </div>
            </Overlay>
          )}

          

          <div style={{ background: 'white', borderRadius: 10, border: '1px solid #E8E8E8', padding: '10px 12px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', marginBottom: 8 }}>Recent transactions</div>
            {/* #761 -- Record production's answer and its Undo, at the top of the list. */}
            <ShelfRunAnswer note={shelfRunNote} onUndo={undoShelfRun} busy={shelfRunBusy} style={{ margin: '0 0 8px' }} />
            {transactions.length === 0 ? (
              <div style={{ fontSize: 12, color: '#9A9A9A' }}>No transactions yet.</div>
            ) : transactions.map(tx => {
              const ts = txStyleFor(tx);
              // #761, T5 -- the history's Correct, reused (682 D14, callout R):
              // full width beneath the line on a phone.
              const fixable = shelfRunCorrectable(correctAuth, tx, shelfFacts);
              return (
                <div key={tx.id} style={{ paddingBottom: 7, marginBottom: 7, borderBottom: '1px solid #F0F0F0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: ts.bg, color: ts.color, flexShrink: 0 }}>{ts.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: tx.qty > 0 ? '#1D9E75' : '#A32D2D', minWidth: 30 }}>{fmtQty(tx.qty)}</span>
                    <span style={{ fontSize: 11, color: '#7A7A7A', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.work_order_ref || '—'}</span>
                    <span style={{ fontSize: 11, color: '#9A9A9A', flexShrink: 0 }}>{fmtDate(tx.created_at)}</span>
                  </div>
                  {fixable && <CorrectButton id={`correct-run-${tx.id}`} onPress={() => setFixingRun(tx)} />}
                </div>
              );
            })}
          </div>

        </div>

        {showAddItem && (
          <AddItemModal categories={categories} vendors={vendors} onClose={() => setShowAddItem(false)}
            onSaved={(newId) => { setShowAddItem(false); setActive('inv_detail'); }} />
        )}

        {/* #515 D32. The same panel the To make queue opens, not a second copy
            — #358's lesson is that a control living in two places gets fixed in
            one of them. `load()` on save, so the strip shows the new figure. */}
        <SetProductionCostPanel
          visible={costPanel}
          onClose={() => setCostPanel(false)}
          onSaved={() => load()}
          isMobile
          item={item}
          auth={{ isAdmin, isManager, isExecutive, isPurchasing, isTeamMember, profile }}
        />

        {/* #761, T5 -- Correct on a Made line opens the correction sheet. */}
        {correctionSheet}
        <RecordProductionSheet
          visible={recordRun}
          onClose={() => setRecordRun(false)}
          onDone={answerShelfRun}
          isMobile
          item={item}
          branchName={branchName}
          qtyOnHand={stockRows.find(r => r.branch_id === userBranchId)?.qty_on_hand ?? 0}
          auth={{ isAdmin, isManager, isExecutive, isPurchasing, isTeamMember, profile }}
        />
      </div>
    );
  }

  // ── DESKTOP ───────────────────────────────────────────────────────────────

  const isNegativeDesktop = homeStockQty < 0;

  return (
    <div onClickCapture={endShelfRunOnPress}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <button onClick={() => setActive('inv_catalog')}
          style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: 'var(--gray-mid)', fontSize: 12, cursor: 'pointer', padding: 0 }}>
          <IconArrowLeft size={13} /> Back to catalog
        </button>
      </div>

      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div>
          <div className="page-title">{item.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <span className="page-sub" style={{ margin: 0 }}>SKU: {item.sku || '—'}</span>
            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: item.is_active ? '#E1F5EE' : '#F0F0F0', color: item.is_active ? '#085041' : '#9A9A9A' }}>
              {item.is_active ? 'Active' : 'Inactive'}
            </span>
            {catParent && (
              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: '#EEEDFE', color: '#3C3489' }}>
                {catParent}{catSub ? ` › ${catSub}` : ''}
              </span>
            )}
            {isNegativeDesktop && (
              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: '#FCEBEB', color: '#A32D2D' }}>
                Audit needed
              </span>
            )}
          </div>
        </div>
      </div>

      {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16, alignItems: 'start' }}>

        {/* ── LEFT COLUMN ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* SPECS */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Specs</div>
              {canEdit && (
                <button className="btn btn-ghost btn-sm" onClick={() => setShowEditSpecs(true)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <IconEdit size={13} /> Edit specs
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' }}>
              {[
                { label: 'UOM',      val: item.uom },
                { label: 'Category', val: catParent || '—' },
                { label: 'Sub',      val: catSub || '—' },
              ].map(({ label, val }) => (
                <div key={label} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11, color: 'var(--gray-lt)', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>{val}</div>
                </div>
              ))}
            </div>
            {item.description && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, color: 'var(--gray-lt)', marginBottom: 3 }}>Description</div>
                <div style={{ fontSize: 13, color: 'var(--gray-mid)', lineHeight: 1.5 }}>{item.description}</div>
              </div>
            )}
          </div>
          <div className="card">
            <VendorItemsPanel
              itemId={item.id}
              itemName={item.name}
              itemUom={item.uom}
              isMobile={false}
              setActive={setActive}
              setFilterItemId={setFilterItemId}
              setFilterItemName={setFilterItemName}
            />
          </div>

          {/* STOCK BY BRANCH */}
          <div className="card">
            {/* #515 D32, desktop. Both strips, in the same order as the phone
                layout — #358's lesson is that a thing rendering in two places
                and only fixed in one is how a screen quietly disagrees with
                itself. */}
            {canSeeCost && isMadeSomewhere && (
              <div style={{ marginBottom: 14 }}>
                <CostToMakeStrip
                  item={item}
                  canChange={maySetProductionCost}
                  onChange={() => setCostPanel(true)}
                />
              </div>
            )}
            {canSeeCost && (
              <div style={{ marginBottom: 14 }}>
                <CostBasisStrip
                  avgCost={stockRows.find(r => r.branch_id === userBranchId)?.avg_unit_cost ?? null}
                  provisionalCount={provisionalReceipts || 0}
                  vendorReceipts={vendorReceipts}
                  transfersIn={transfersIn}
                />
              </div>
            )}
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Stock by branch</div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Branch</th>
                    <th style={{ width: 70 }}>Qty</th>
                    <th style={{ width: 90 }}>Status</th>
                    <th style={{ width: 110 }}>Reorder pt</th>
                    <th style={{ width: 110 }}>Reorder qty</th>
                    <th>Location note</th>
                  </tr>
                </thead>
                <tbody>
                  {BRANCHES.map(branch => {
                    const s = stockRows.find(r => r.branch_id === branch.id);
                    const qty = s?.qty_on_hand ?? 0;
                    const isNeg = qty < 0;
                    const status = s ? getStockStatus(qty, s.reorder_point) : null;
                    const st = status ? STATUS_STYLES[status] : null;
                    const editable = isEditable(branch.id);
                    const edits = reorderEdits[branch.id] || { reorder_point: '', reorder_qty: '' };
                    // #512 -- drawing B, desk: under the branch name, not a
                    // new column (B3), at Stock Home's 11px.
                    const coming = comingLinesFor(branch.id);
                    return (
                      <tr key={branch.id}>
                        <td style={{ fontWeight: 700, fontSize: 13 }}>
                          {branch.name}
                          {coming.map((l, i) => <div key={i} style={COMING_LINE}>{l}</div>)}
                        </td>
                        {/* #782 -- no stock record here means the branch doesn't
                            stock it, which is "—" as on the phone, never "0". */}
                        <td style={{ fontWeight: 700, color: !s ? 'var(--ink-3)' : isNeg ? '#A32D2D' : st ? st.color : 'var(--black)' }}>{s ? qty : '—'}</td>
                        <td>
                          {isNeg ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#A32D2D', display: 'inline-block' }} />
                              <span style={{ fontSize: 11, fontWeight: 700, color: '#A32D2D' }}>Audit needed</span>
                            </span>
                          ) : st ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: st.dot, display: 'inline-block' }} />
                              <span style={{ fontSize: 11, fontWeight: 700, color: st.color }}>{status}</span>
                            </span>
                          ) : <span style={{ color: 'var(--gray-lt)', fontSize: 12 }}>—</span>}
                        </td>
                        <td>
                          {editable ? (
                            <input type="number" min={0} value={edits.reorder_point}
                              onChange={e => setReorderEdits(prev => ({ ...prev, [branch.id]: { ...prev[branch.id], reorder_point: e.target.value } }))}
                              placeholder="—" style={{ width: 70, height: 28, border: '1px solid var(--border)', borderRadius: 6, padding: '0 6px', fontSize: 16, color: 'var(--black)' }} />
                          ) : (
                            <span style={{ fontSize: 12, color: 'var(--gray-mid)' }}>{s?.reorder_point ?? '—'}</span>
                          )}
                        </td>
                        <td>
                          {editable ? (
                            <input type="number" min={0} value={edits.reorder_qty}
                              onChange={e => setReorderEdits(prev => ({ ...prev, [branch.id]: { ...prev[branch.id], reorder_qty: e.target.value } }))}
                              placeholder="—" style={{ width: 70, height: 28, border: '1px solid var(--border)', borderRadius: 6, padding: '0 6px', fontSize: 16, color: 'var(--black)' }} />
                          ) : (
                            <span style={{ fontSize: 12, color: 'var(--gray-mid)' }}>{s?.reorder_qty ?? '—'}</span>
                          )}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--gray-mid)' }}>{s?.location_note || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {incomingFailed && (
              <div role="status" style={{ marginTop: 6, fontSize: 11, color: '#8A5A08' }}>{INCOMING_FAILED}</div>
            )}
            {canAdjust && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
                <span style={{ fontSize: 11, color: 'var(--gray-lt)', fontStyle: 'italic' }}>
                  {isBranchScoped ? 'Editing your home branch only. Other branches are read-only.' : ''}
                </span>
                <button className="btn btn-primary btn-sm" onClick={handleSaveReorder} disabled={savingReorder} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {reorderSaved ? <><IconCheck size={13} /> Saved</> : savingReorder ? 'Saving...' : 'Save reorder settings'}
                </button>
              </div>
            )}
          </div>

          {/* TRANSACTION LOG */}
          <div className="card">
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Transaction log · last 10
            </div>
            {/* #761 -- Record production's answer and its Undo, at the top of the list. */}
            <ShelfRunAnswer note={shelfRunNote} onUndo={undoShelfRun} busy={shelfRunBusy} style={{ margin: '0 0 8px' }} />
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>Type</th>
                    <th style={{ width: 60 }}>Qty</th>
                    <th style={{ width: 70 }}>After</th>
                    <th>WO#</th>
                    <th style={{ width: 100 }}>Date</th>
                    {/* #761, T5 -- a narrow column at the right carries Correct, as
                        the transfer's history does at a desk (callout N). */}
                    {anyFixable && <th aria-label="Correct" style={{ width: 1 }} />}
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr><td colSpan={anyFixable ? 6 : 5} style={{ textAlign: 'center', color: 'var(--gray-lt)', fontSize: 12, padding: '20px 0' }}>No transactions yet.</td></tr>
                  ) : transactions.map(tx => {
                    const ts = txStyleFor(tx);
                    const fixable = shelfRunCorrectable(correctAuth, tx, shelfFacts);
                    return (
                      <tr key={tx.id}>
                        <td><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: ts.bg, color: ts.color }}>{ts.label}</span></td>
                        <td style={{ fontWeight: 700, color: tx.qty > 0 ? '#1D9E75' : '#A32D2D', fontSize: 13 }}>{fmtQty(tx.qty)}</td>
                        <td style={{ fontSize: 12, color: 'var(--gray-mid)' }}>{tx.qty_after ?? '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--gray-mid)' }}>{tx.work_order_ref || '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--gray-lt)' }}>{fmtDate(tx.created_at)}</td>
                        {anyFixable && (
                          <td style={{ textAlign: 'right', paddingLeft: 6 }}>
                            {fixable && <CorrectButton id={`correct-run-${tx.id}`} desk coarse={coarse} onPress={() => setFixingRun(tx)} />}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--gray-lt)', fontStyle: 'italic' }}>
              {txCount > 10 ? `Showing 10 of ${txCount} transactions · read-only` : 'Read-only'}
            </div>
          </div>

        </div>

        {/* ── RIGHT COLUMN ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* IMAGE */}
          <div className="card" style={{ padding: 14 }}>
            <div style={{ width: '100%', aspectRatio: '3/4', background: 'var(--off-white)', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, border: '1px dashed var(--border)', overflow: 'hidden', marginBottom: 10 }}>
              {item.image_url ? (
                <StoredImage value={item.image_url} bucket={INVENTORY_BUCKET} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <>
                  <IconPhotoOff size={28} color="var(--gray-lt)" />
                  <span style={{ fontSize: 12, color: 'var(--gray-lt)' }}>No image</span>
                </>
              )}
            </div>
            {canEdit && (
              <>
                <input ref={imageFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
                <button onClick={() => imageFileRef.current.click()} disabled={uploadingImage} className="btn btn-ghost btn-sm"
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  <IconUpload size={13} /> {uploadingImage ? 'Uploading...' : 'Upload image'}
                </button>
                <div style={{ fontSize: 10, color: 'var(--gray-lt)', textAlign: 'center', marginTop: 4 }}>Visible as thumbnail in catalog</div>
              </>
            )}
          </div>

          {/* RECEIVE / USE STOCK */}
          <div className="card" style={{ padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Stock actions</div>

            {!showReceiveSheet && !showUseSheet && !showAdjustSheet && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {receiveMatchedRequest && (
                  <div style={{ background: '#E6F1FB', border: '1px solid #B5D4F4', borderRadius: 8, padding: '8px 10px', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <IconInfoCircle size={13} color="#0C447C" style={{ flexShrink: 0, marginTop: 1 }} />
                    <div style={{ fontSize: 11, color: '#0C447C' }}>
                      <span style={{ fontWeight: 700 }}>Open order:</span> {receiveMatchedRequest.request_number}
                    </div>
                  </div>
                )}
                <button className="btn btn-sm" onClick={openReceiveSheet}
                  style={{ width: '100%', background: '#1D9E75', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  + Receive stock
                </button>
                <button className="btn btn-sm" onClick={openUseSheet}
                  style={{ width: '100%', background: '#0F0F0F', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  − Use stock
                </button>
                {/* #364 — Adjust was absent from desktop entirely. Receive and
                    Use were both here; the one action that reconciles the count
                    against what is physically on the shelf was the omission, on
                    the screen that shows stock by branch and the ledger. */}
                {canAdjust && (
                  <button className="btn btn-sm" onClick={openAdjustSheet}
                    style={{ width: '100%', background: 'white', color: '#0F0F0F', border: '1px solid #E0E0E0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                    Adjust count
                  </button>
                )}
                {/* #515 — see the note on the phone layout above. Both layouts
                    get it; #358 is the record of what happens when only one
                    does, and this file draws the whole screen twice. */}
                {myBranchMakesThis && (
                  <button className="btn btn-sm" onClick={() => setRecordRun(true)}
                    style={{ width: '100%', background: 'white', color: '#0F0F0F', border: '1px solid #E0E0E0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                    Record production
                  </button>
                )}
              </div>
            )}

            {showReceiveSheet && (
              <div style={{ marginTop: 4 }}>
                <ReceiveSheetContent
                  itemName={item.name}
                  itemId={item.id ?? null}
                  itemUom={item.uom || null}
                  branchName={branchName}
                  matchedRequest={receiveMatchedRequest}
                  canOverride={canOverrideJobRef}
                  printDescription={receivePrintDescription}
                  printAvailable={!!receivePrintOption}
                  packQty={receivePack.packQty}
                  packUnit={receivePack.packUnit}
                  packVendorName={receivePack.vendorName}
                  onSubmit={submitReceive}
                  onCancel={() => setShowReceiveSheet(false)}
                  saving={receiveSaving}
                  done={receiveDone}
                  orderClosed={receiveOrderClosed}
                  photoFailed={receivePhotoFailed}
                  serverError={receiveError}
                />
              </div>
            )}

            {showUseSheet && (
              <div style={{ marginTop: 4 }}>
                <UseSheetContent
                  itemName={item.name}
                  branchName={branchName}
                  homeStockQty={homeStockQty}
                  itemUom={item.uom}
                  onFetchJobHistory={fetchJobHistory}
                  onSubmit={submitUse}
                  onCancel={() => setShowUseSheet(false)}
                  saving={useSaving}
                  done={useDone}
                  serverError={useError}
                  requireJobRef={requireJobRefOnUse}
                />
              </div>
            )}

            {/* #364 — desktop renders the sheet inline in the Stock Actions
                card, the same way Receive and Use do here, rather than in an
                Overlay. Mobile uses the Overlay sheet above. */}
            {showAdjustSheet && (
              <div style={{ marginTop: 4 }}>
                <AdjustSheetContent
                  itemName={item.name}
                  branchName={branchName}
                  currentQty={homeStockQty}
                  itemUom={item.uom}
                  userName={profile?.full_name}
                  onSubmit={submitAdjust}
                  onCancel={() => setShowAdjustSheet(false)}
                  saving={adjustSaving}
                  done={adjustDone}
                  doneReason={adjustDoneReason}
                  doneLine={adjustDoneText}
                  serverError={adjustError}
                  itemId={itemId}
                  branchId={userBranchId}
                  canSeeCost={canSeeCost}
                />
              </div>
            )}
          </div>

          {/* LABEL */}
          <LabelCard item={item} branchName={branchName} isMobile={false} />
          {/* RESTOCK REQUESTS */}
          <MaterialRequestsPanel
            itemId={itemId}
            itemName={item?.name || ''}
            branchId={userBranchId}
            compact={false}
            canManage={canManageRequests}
            setActive={setActive}
            setOpenRequestId={setOpenRequestId}
            onRequestsChanged={updated => {
              const orderedReq = updated.find(r => r.status === 'ordered') || null;
              setReceiveMatchedRequest(orderedReq);
            }}
          />
          <div style={{ border: '0.5px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '12px 14px', marginTop: 8 }}>
            {restockDone ? (
              <div style={{ background: '#E8F5E9', borderRadius: 8, padding: '10px 12px', border: '1px solid #C8E6C9', textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#2E7D32', marginBottom: 2 }}>✓ Request submitted</div>
                <div style={{ fontSize: 11, color: '#5C5C5C' }}>Your manager has been notified.</div>
                <button onClick={() => { setRestockDone(false); setRestockJobRef(''); }} style={{ marginTop: 8, background: 'none', border: 'none', fontSize: 11, color: '#1D9E75', cursor: 'pointer', textDecoration: 'underline' }}>Submit another</button>
              </div>
            ) : (requestRt.route !== 'buy' && !restockBuyInstead) ? (
              /* #684 G -- A MADE ITEM LEADS WITH ITS ROUTE. This form submitted a
                 purchase request directly, without visiting the request screen,
                 so it was the one door the request screen's own routing could
                 not reach. The button opens the Ask sheet (or Make these
                 ourselves at the maker) through the request screen, exactly as
                 the phone buttons do; the purchase form waits behind the same
                 sentence every other door uses. A mouse surface: 32px to press,
                 nothing below 24 (#417 D2). */
              <div style={{ background: 'var(--off-white)', borderRadius: 8, border: '0.5px solid var(--border)', padding: '10px 12px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 8 }}>New request</div>
                <div style={{ fontSize: 12, color: '#14503B', marginBottom: 8 }}>
                  {requestRt.route === 'make_own' ? 'We make this here.' : `${requestMaker} makes this.`}
                </div>
                <button onClick={openRestockForm}
                  style={{ padding: '0 14px', minHeight: 32, background: '#0F6E56', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  {requestRt.route === 'make_own' ? 'Make these ourselves' : `Ask ${requestMaker} to make these`}
                </button>
                <div style={{ marginTop: 6 }}>
                  <button type="button" onClick={() => setRestockBuyInstead(true)}
                    style={{ background: 'none', border: 'none', padding: '0 2px', minHeight: 24, font: 'inherit', fontSize: 12, color: '#3A3A3A', textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}>
                    Buy from a vendor instead?
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ background: 'var(--off-white)', borderRadius: 8, border: '0.5px solid var(--border)', padding: '10px 12px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', marginBottom: 8 }}>New request</div>
                {/* #684 E -- a made item being bought on purpose says so, with the
                    way back to its maker. */}
                {requestRt.route !== 'buy' && (
                  <div style={{ fontSize: 12, color: '#633806', marginBottom: 8 }}>
                    Buying from a vendor. {requestRt.route === 'make_own' ? 'We make these.' : `${requestMaker} makes these.`}{' '}
                    <button type="button" onClick={() => setRestockBuyInstead(false)}
                      style={{ background: 'none', border: 'none', padding: '0 2px', minHeight: 24, font: 'inherit', fontSize: 12, color: '#3A3A3A', textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}>
                      {requestRt.route === 'make_own' ? 'Make these ourselves instead' : `Ask ${requestMaker} instead`}
                    </button>
                  </div>
                )}
                {restockError && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 6, padding: '6px 10px', fontSize: 11, marginBottom: 8 }}>{restockError}</div>}
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--gray-mid)', marginBottom: 3 }}>Qty</div>
                    <input type="number" min={1} value={restockQty} onChange={e => setRestockQty(e.target.value)}
                      style={{ width: 70, height: 32, border: '1px solid var(--border)', borderRadius: 6, padding: '0 8px', fontSize: 16, color: 'var(--black)' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: 'var(--gray-mid)', marginBottom: 3 }}>WO# (optional)</div>
                    <input type="text" value={restockJobRef} onChange={e => setRestockJobRef(e.target.value)} placeholder="e.g. 123456 or 123456-1"
                      style={{ width: '100%', height: 32, border: '1px solid var(--border)', borderRadius: 6, padding: '0 8px', fontSize: 16, color: 'var(--black)' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={submitRestockRequest} disabled={restockSaving}
                    style={{ padding: '6px 16px', background: restockSaving ? '#E0E0E0' : '#1D9E75', color: restockSaving ? '#9A9A9A' : 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: restockSaving ? 'not-allowed' : 'pointer' }}>
                    {restockSaving ? 'Submitting...' : 'Submit request'}
                  </button>
                </div>
                <div style={{ fontSize: 10, color: 'var(--gray-lt)', marginTop: 6, fontStyle: 'italic' }}>Goes to your branch manager for approval</div>
              </div>
            )}
          </div>

          {/* ADD ITEM */}
          {canEdit && (
            <div className="card" style={{ padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Add item</div>
              <div style={{ fontSize: 12, color: 'var(--gray-lt)', marginBottom: 10, lineHeight: 1.5 }}>Add a new item to the catalog across all branches.</div>
              <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={() => setShowAddItem(true)}>+ Add item</button>
            </div>
          )}

          {/* DEACTIVATE / DELETE */}
          {canEdit && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {item.is_active ? (
                <button className="btn btn-sm" onClick={handleDeactivate} disabled={deactivating}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, background: '#FFEBEE', color: '#A32D2D', border: '1px solid #F09595' }}>
                  <IconArchive size={13} /> {deactivating ? 'Deactivating...' : 'Deactivate item'}
                </button>
              ) : (
                <button className="btn btn-sm" onClick={handleReactivate}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, background: '#E1F5EE', color: '#085041', border: '1px solid #5DCAA5' }}>
                  <IconCheck size={13} /> Reactivate item
                </button>
              )}
              {txCount === 0 && (
                confirmDelete ? (
                  <div style={{ background: '#FFEBEE', border: '1px solid #F09595', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 12, color: '#A32D2D', fontWeight: 700, marginBottom: 6 }}>Delete permanently?</div>
                    <div style={{ fontSize: 11, color: '#A32D2D', marginBottom: 8 }}>This cannot be undone. All stock records will be removed.</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setConfirmDelete(false)} className="btn btn-ghost btn-sm" style={{ flex: 1 }}>Cancel</button>
                      <button onClick={handleDelete} disabled={deleting} className="btn btn-sm" style={{ flex: 1, background: '#A32D2D', color: 'white', border: 'none' }}>
                        {deleting ? 'Deleting...' : 'Yes, delete'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="btn btn-sm" onClick={() => setConfirmDelete(true)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, color: '#A32D2D', border: '1px solid #F09595' }}>
                    <IconTrash size={13} /> Delete item
                  </button>
                )
              )}
              <div style={{ fontSize: 10, color: 'var(--gray-lt)', fontStyle: 'italic', textAlign: 'right' }}>
                {txCount > 0 ? 'Has transaction history — deactivate only' : 'No transactions — hard delete available'}
              </div>
            </div>
          )}

        </div>
      </div>

      {showAddItem && (
        <AddItemModal categories={categories} vendors={vendors} onClose={() => setShowAddItem(false)} onSaved={() => { setShowAddItem(false); }} />
      )}

      {showEditSpecs && (
        <EditSpecsModal item={item} categories={categories} vendors={vendors} onClose={() => setShowEditSpecs(false)} onSaved={() => { setShowEditSpecs(false); load(); }} />
      )}

      {/* #515 D32, desktop. Both layouts render it, because this file draws the
          whole screen twice and #358 is the record of what happens when only
          one copy gets the change. */}
      <SetProductionCostPanel
        visible={costPanel}
        onClose={() => setCostPanel(false)}
        onSaved={() => load()}
        isMobile={false}
        item={item}
        auth={{ isAdmin, isManager, isExecutive, isPurchasing, isTeamMember, profile }}
      />

      {/* #761, T5 -- Correct on a Made line opens the correction sheet. */}
      {correctionSheet}
      <RecordProductionSheet
        visible={recordRun}
        onClose={() => setRecordRun(false)}
        onDone={answerShelfRun}
        isMobile={false}
        item={item}
        branchName={branchName}
        qtyOnHand={stockRows.find(r => r.branch_id === userBranchId)?.qty_on_hand ?? 0}
        auth={{ isAdmin, isManager, isExecutive, isPurchasing, isTeamMember, profile }}
      />
    </div>
  );
}
